import { Keypair, StrKey, hash } from "@stellar/stellar-sdk";
import { Buffer } from "buffer";

/** Signed public receiving metadata: G-address -> existing stv3 receiving address. */
export interface DirectoryRegistration {
  version: 1;
  account: string;
  networkPassphrase: string;
  contractId: string;
  assetContractId: string;
  receivingAddress: string;
  issuedAt: number;
  signature: string;
}
export interface DirectoryBinding {
  networkPassphrase: string;
  contractId: string;
  assetContractId: string;
}

const MAX_FUTURE_SECONDS = 300;

export function registrationMessage(
  r: Omit<DirectoryRegistration, "signature" | "version">,
): string {
  return [
    "StealthOS private receiving address registration v1",
    "Network: " + r.networkPassphrase,
    "Pool: " + r.contractId,
    "Asset: " + r.assetContractId,
    "Account: " + r.account,
    "Receiving address: " + r.receivingAddress,
    "Issued: " + r.issuedAt,
    "Signing lets StealthOS senders resolve this account to the receiving address above. It does not move funds.",
  ].join("\n");
}

export function signatureValid(account: string, message: string, signature: Uint8Array) {
  if (signature.length !== 64) return false;
  const key = Keypair.fromPublicKey(account);
  const raw = Buffer.from(message, "utf8");
  // Freighter signs the SEP-53 prefixed hash; older builds signed the raw bytes.
  return (
    key.verify(hash(Buffer.concat([Buffer.from("Stellar Signed Message:\n"), raw])), Buffer.from(signature)) ||
    key.verify(raw, Buffer.from(signature))
  );
}

export function signatureBytes(signed: any): Uint8Array {
  return typeof signed === "string"
    ? Uint8Array.from(Buffer.from(signed, "base64"))
    : signed instanceof Uint8Array
      ? Uint8Array.from(signed)
      : signed?.data
        ? Uint8Array.from(signed.data)
        : new Uint8Array();
}

/** Throws unless the entry is a well-formed, correctly signed registration for this exact pool. */
export function verifyRegistration(
  value: unknown,
  binding: DirectoryBinding,
  account?: string,
  now = Date.now(),
): DirectoryRegistration {
  const r = value as DirectoryRegistration;
  const fail = (message: string): never => {
    throw Error(message);
  };
  if (
    !r ||
    typeof r !== "object" ||
    Object.keys(r).sort().join(",") !==
      "account,assetContractId,contractId,issuedAt,networkPassphrase,receivingAddress,signature,version" ||
    r.version !== 1
  )
    fail("Recipient registration is malformed.");
  if (!StrKey.isValidEd25519PublicKey(r.account) || (account && r.account !== account))
    fail("Recipient registration belongs to a different account.");
  if (
    r.networkPassphrase !== binding.networkPassphrase ||
    r.contractId !== binding.contractId ||
    r.assetContractId !== binding.assetContractId
  )
    fail("Recipient registration is for a different network, pool or asset.");
  if (typeof r.receivingAddress !== "string" || !/^stv3:[a-f0-9]{404}$/.test(r.receivingAddress))
    fail("Recipient registration has no usable private receiving address.");
  if (!Number.isSafeInteger(r.issuedAt) || r.issuedAt <= 0 || r.issuedAt > now / 1000 + MAX_FUTURE_SECONDS)
    fail("Recipient registration time is invalid.");
  if (typeof r.signature !== "string" || !/^[A-Za-z0-9+/]{86}==$/.test(r.signature))
    fail("Recipient registration signature is malformed.");
  const { signature, version, ...body } = r;
  if (!signatureValid(r.account, registrationMessage(body), signatureBytes(signature)))
    fail("Recipient registration signature is invalid.");
  return r;
}
